// Agent design showcase: every component the conversation column can draw, on
// one page, from fixed data — no session, no sandbox, no model. Open it at
// /agent/:customer/design when changing how the message list looks, so a
// visual change can be judged against every message shape at once instead of
// hunting for a real session that happens to contain a failed tool, an
// unanswered question and a stopped turn.
//
// It renders the real `AgentChat` from frontend/src/agent with props instead of
// a provider, the same way the golden-path test does, and registers the same
// render plugins the chat page does, fed with real payloads captured from a
// bprsky session (designShowcaseFixtures.ts) — so the table, chart, composite
// table, provenance checks and dashboard on this page are the real widgets
// against the real job, toolbars and all (Adam, 11 Sep: "we need all the
// functionality"). The checks panel appears twice, once per phase. One honest
// gap: an artifact whose preview needs the workspace (code, CSV, report, story
// board) is left out, because there is no workspace to read it from.
//
// Both side panels are here too, wired as the chat page wires them: the left
// panel's Jobs / Tables / Variables tabs are the real ones against the job, so
// ticking a table chips the composer for real; its session history is fixed.
// The right panel is AgentChat's own Files panel over the showcase artifacts,
// headed by the Agents section over the Chief of Staff fixtures: their
// workflow runs and agents' conversations come from a fixed
// `WorkflowAgentsSource`, so a click on an agent opens the real drawer.
//
// The first showcase lived at this route on the v1 components and went with
// them in the agentkit rewire (d2d4fa271). This is its successor.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import { AgentChat, AgentSidePanel, EdgeReveal, SidePanelLauncher, ActivityOrb, orbStateFor, AgentCardHostContext, DEFAULT_CARD_HOST, agentEventReducer, initialAgentEventState } from '../agent'
import type { OrbState } from '../agent'
import type { WorkflowRun, WorkflowTranscript } from '../agent/types'
import { WorkflowAgentsSourceContext, type WorkflowAgentsSource } from '../agent/hooks/useWorkflowAgents'
import VerticalSplitOutlinedIcon from '@mui/icons-material/VerticalSplitOutlined'
import type {
  ActivityStatus,
  AgentMessage,
  AgentSSEEvent,
  AgentSessionListItem,
  AgentSidePanelTab,
  ArtifactInfo,
  AskUserQuestionInfo,
  TodoItem,
} from '../agent'
import { AGENT_COLUMN_MAX_WIDTH } from '../components/agent/layout'
import AgentAttachmentPreview from '../components/agent/AgentAttachmentPreview'
import { buildAgentSidePanelTabs } from '../components/agent/agentSidePanelTabs'
import useComposerChips, { type ComposerChip } from '../hooks/useComposerChips'
import useAccount from '../hooks/useAccount'
import { useJobLabel } from '../utils/displayNames'
import { AGENT_PLUGINS } from './AgentChatPageV2'
import ChatDrawer from '../components/chat/ChatDrawer'
import type { ChatMessage } from '../components/chat/types'
import { CARD_SX, HAIRLINE } from '../agent'
import AlertSetupCard from '../components/alerts/AlertSetupCard'
import type { GenNode } from '../types/carbon/models/GenNode'
import type { AlertSetupDraft } from '../utils/alertSetupDraft'
import { ANALYSIS_FLAGGED, BRAND_GAP_SCRIPT, WAREHOUSE_QUERY_ERROR, WAREHOUSE_QUERY_OK, CHART_BY_AGE, COMPOSITE_TABLE, DASHBOARD_CREATED, PROVENANCE_COMPLETE, PROVENANCE_INITIAL, TABLE_BY_AGE, TABLE_BY_GENDER } from './designShowcaseFixtures'
import NotebookDialog from '../components/agent/notebook/NotebookDialog'
import { deriveNotebookCells } from '../components/agent/notebook/notebookCells'
import type { NotebookState } from '../components/agent/notebook/notebookTypes'
import type { NotebookController } from '../components/agent/notebook/useNotebook'
import { warehouseCardActionReason } from '../components/agent/WarehouseQueryCard'
import { NOBODY_KNOWN, refuse } from '../utils/permissionReason'

const SESSION_ID = 'design-showcase'
const CUSTOMER = 'bprsky'
const JOB = 'sky-uk-be-prius'
const at = (secondsAgo: number) => new Date(Date.now() - secondsAgo * 1000).toISOString()

// A small SVG bar chart, base64-encoded, so the screenshot tool's image path
// (view-image → inline preview) has a real picture to draw.
const CHART_SVG_BASE64 = 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0ODAiIGhlaWdodD0iMjIwIiB2aWV3Qm94PSIwIDAgNDgwIDIyMCI+PHJlY3Qgd2lkdGg9IjQ4MCIgaGVpZ2h0PSIyMjAiIGZpbGw9IiNmZmZmZmYiLz48dGV4dCB4PSIyNCIgeT0iMzAiIGZvbnQtZmFtaWx5PSItYXBwbGUtc3lzdGVtLFNlZ29lIFVJLHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIGZvbnQtd2VpZ2h0PSI2MDAiIGZpbGw9IiMwQTE2MjgiPkJyYW5kIGF3YXJlbmVzcyBieSByZWdpb248L3RleHQ+PGcgZmlsbD0iIzI2Mzc1NyI+PHJlY3QgeD0iNDAiIHk9IjYwIiB3aWR0aD0iNTYiIGhlaWdodD0iMTMwIi8+PHJlY3QgeD0iMTIwIiB5PSI3MiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjExOCIvPjxyZWN0IHg9IjIwMCIgeT0iODYiIHdpZHRoPSI1NiIgaGVpZ2h0PSIxMDQiLz48cmVjdCB4PSIyODAiIHk9Ijk0IiB3aWR0aD0iNTYiIGhlaWdodD0iOTYiLz48cmVjdCB4PSIzNjAiIHk9Ijg4IiB3aWR0aD0iNTYiIGhlaWdodD0iMTAyIi8+PC9nPjxnIGZvbnQtZmFtaWx5PSItYXBwbGUtc3lzdGVtLFNlZ29lIFVJLHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTEiIGZpbGw9IiM2NDc0OEIiIHRleHQtYW5jaG9yPSJtaWRkbGUiPjx0ZXh0IHg9IjY4IiB5PSIyMDYiPkxvbmRvbjwvdGV4dD48dGV4dCB4PSIxNDgiIHk9IjIwNiI+U291dGggRWFzdDwvdGV4dD48dGV4dCB4PSIyMjgiIHk9IjIwNiI+TWlkbGFuZHM8L3RleHQ+PHRleHQgeD0iMzA4IiB5PSIyMDYiPk5vcnRoPC90ZXh0Pjx0ZXh0IHg9IjM4OCIgeT0iMjA2Ij5TY290bGFuZDwvdGV4dD48L2c+PGcgZm9udC1mYW1pbHk9Ii1hcHBsZS1zeXN0ZW0sU2Vnb2UgVUksc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMSIgZmlsbD0iIzBBMTYyOCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC13ZWlnaHQ9IjYwMCI+PHRleHQgeD0iNjgiIHk9IjUyIj44NSU8L3RleHQ+PHRleHQgeD0iMTQ4IiB5PSI2NCI+NzklPC90ZXh0Pjx0ZXh0IHg9IjIyOCIgeT0iNzgiPjcyJTwvdGV4dD48dGV4dCB4PSIzMDgiIHk9Ijg2Ij42OCU8L3RleHQ+PHRleHQgeD0iMzg4IiB5PSI4MCI+NzElPC90ZXh0PjwvZz48L3N2Zz4='

const MODELS = [
  { id: 'opus', label: 'Best', costMultiplier: 1 },
  { id: 'sonnet', label: 'Good', costMultiplier: 0.5 },
]

const messages: AgentMessage[] = [
  {
    id: 'm1',
    role: 'user',
    content: 'Analyse brand awareness by region, chart it, and prepare a PowerPoint export.',
    timestamp: at(600),
  },
  {
    id: 'm2',
    role: 'assistant',
    thinking: 'The reader wants a regional breakdown of brand awareness with a chart and a PowerPoint export. Start with the table of contents to find the awareness table, run a crosstab with Region on top, chart it, then build the deck.',
    content: "I'll start by finding the brand awareness table and running the regional breakdown.",
    timestamp: at(590),
  },
  {
    id: 'm3',
    role: 'assistant',
    content: '',
    timestamp: at(580),
    toolCalls: [
      { id: 'tc-toc', name: 'get_toc', input: { customer: 'bprsky', job: 'sky-uk-be-prius' }, output: JSON.stringify({ sections: 3, tables: 412 }), status: 'complete', elapsedSeconds: 0.8 },
      { id: 'tc-table', name: 'run_table_spec', input: { top: 'gender(1/2)', side: 'BrandConsideration(33)', title: 'Sky consideration rate by gender' }, output: JSON.stringify({ status: 'rendered', rows: 2, columns: 3 }), status: 'complete', elapsedSeconds: 2.4 },
      { id: 'tc-chart', name: 'render_chart', input: { chartType: 'bar', top: 'ageband1(*)', side: 'BrandConsideration(33)', title: 'Sky consideration by age group' }, output: JSON.stringify({ status: 'rendered' }), status: 'complete', elapsedSeconds: 1.2 },
    ],
  },
  {
    id: 'm4',
    role: 'assistant',
    content: "Here's the regional picture. **Key findings:**\n\n- London leads at **85%** total awareness\n- Scotland shows the strongest growth at **+5pp**\n- The North is the only region in decline (**−1pp**)\n\n| Region | Total | Spontaneous | Prompted |\n|---|---|---|---|\n| London | 85% | 42% | 43% |\n| South East | 79% | 38% | 41% |\n| Midlands | 72% | 31% | 41% |\n| North | 68% | 28% | 40% |\n| Scotland | 71% | 35% | 36% |",
    timestamp: at(570),
  },
  {
    id: 'm5',
    role: 'assistant',
    content: '',
    timestamp: at(560),
    toolCalls: [
      { id: 'tc-write', name: 'text_editor', input: { command: 'create', file_path: 'analysis/brand_report.py', file_text: "import pandas as pd\n\ndf = pd.read_csv('awareness_by_region.csv')\nsummary = df.groupby('region')['awareness'].mean().sort_values(ascending=False)\nprint(summary.to_string())\n" }, output: 'File created', status: 'complete', elapsedSeconds: 0.3 },
    ],
  },
  {
    // A page with its chart inlined as a data URI: one line of ~200k
    // characters in the real thing. What the block must show is a readable
    // page with a marker where the payload is (Matt, 11 Sep).
    id: 'm5b',
    role: 'assistant',
    content: '',
    timestamp: at(575),
    toolCalls: [
      { id: 'tc-write-html', name: 'text_editor', input: { command: 'create', file_path: 'data/brand_report.html', file_text: `<!doctype html>\n<html>\n  <body>\n    <h1>Brand awareness, Aug-26</h1>\n    <img alt="Awareness by region" src="data:image/png;base64,${'iVBORw0KGgoAAAANSUhEUg'.repeat(9000)}">\n    <p>Sky leads on spontaneous awareness in every region except the North East.</p>\n  </body>\n</html>\n` }, output: 'File created', status: 'complete', elapsedSeconds: 0.6 },
      // The same page read back as plain text. A raw result has no file
      // block, so this is the generic output path collapsing the image.
      { id: 'tc-cat-html', name: 'Bash', input: { command: 'cat data/brand_report.html' }, output: `<!doctype html>\n<html>\n  <body>\n    <h1>Brand awareness, Aug-26</h1>\n    <img alt="Awareness by region" src="data:image/png;base64,${'iVBORw0KGgoAAAANSUhEUg'.repeat(9000)}">\n    <p>Sky leads on spontaneous awareness in every region except the North East.</p>\n  </body>\n</html>\n`, status: 'complete', elapsedSeconds: 0.1 },
    ],
  },
  {
    id: 'm6',
    role: 'assistant',
    content: '',
    timestamp: at(550),
    toolCalls: [
      { id: 'tc-run', name: 'Bash', input: { command: 'python3 analysis/brand_report.py' }, output: 'region\nLondon        85.0\nSouth East    79.0\nMidlands      72.0\nScotland      71.0\nNorth         68.0', status: 'complete', elapsedSeconds: 1.9 },
    ],
  },
  {
    id: 'm6b',
    role: 'assistant',
    content: 'And a table I assembled from the two runs, with provenance on every cell:',
    timestamp: at(545),
    toolCalls: [
      { id: 'tc-compose', name: 'compose_table', input: { name: 'brand_index', title: 'Brand index — assembled' }, output: JSON.stringify({ status: 'rendered', cells: 4 }), status: 'complete', elapsedSeconds: 0.2 },
    ],
  },
  {
    // validate_provenance, both calls. Two tool calls, so two cards: the
    // checkpoint that is still replaying its derived cells, then the verdict
    // with a failure in it. Nothing supersedes anything — see the plugin's
    // header comment.
    id: 'm6c',
    role: 'assistant',
    content: 'And I checked every cell of it back against its source:',
    timestamp: at(544),
    toolCalls: [
      { id: 'tc-validate-1', name: 'validate_provenance', input: { name: 'brand_index' }, output: JSON.stringify({ phase: 'initial', pending: 3 }), status: 'complete', elapsedSeconds: 0.4 },
      { id: 'tc-validate-2', name: 'validate_provenance', input: { name: 'brand_index', replayed: 3 }, output: JSON.stringify({ phase: 'complete', failed: 1 }), status: 'complete', elapsedSeconds: 0.5 },
      { id: 'tc-flag-1', name: 'flag_advanced_analysis', input: { method: 'key_driver', summary: ANALYSIS_FLAGGED.summary }, output: JSON.stringify({ line: ANALYSIS_FLAGGED.line }), status: 'complete', elapsedSeconds: 0.2 },
      { id: 'tc-wh-1', name: 'warehouse_query', input: { connection: 'Sky BigQuery', sql: WAREHOUSE_QUERY_OK.sql, title: WAREHOUSE_QUERY_OK.title, save_as: 'viewers_by_region.json' }, output: 'Ran the query on Sky BigQuery (bigquery). 12 rows.', status: 'complete', elapsedSeconds: 1.9 },
      { id: 'tc-wh-2', name: 'warehouse_query', input: { connection: 'Sky BigQuery', sql: WAREHOUSE_QUERY_ERROR.sql }, output: `The query on Sky BigQuery failed: ${WAREHOUSE_QUERY_ERROR.error}`, status: 'complete', elapsedSeconds: 0.4 },
    ],
  },
  {
    id: 'm7',
    role: 'assistant',
    content: 'I rendered the chart and captured it:',
    timestamp: at(540),
    toolCalls: [
      { id: 'tc-shot', name: 'view_image', input: { file_path: 'charts/awareness_by_region.svg' }, output: JSON.stringify({ type: 'image', source: { data: CHART_SVG_BASE64, media_type: 'image/svg+xml' } }), status: 'complete', elapsedSeconds: 0.6 },
    ],
  },
  {
    id: 'm8',
    role: 'assistant',
    content: '',
    timestamp: at(530),
    toolCalls: [
      { id: 'tc-ask-format', name: 'ask_user_question', input: { question: 'Which slide format?' }, output: '', status: 'complete', elapsedSeconds: 0.1 },
    ],
  },
  { id: 'm9', role: 'user', content: 'Widescreen, please.', timestamp: at(520) },
  {
    id: 'm10',
    role: 'assistant',
    content: '',
    timestamp: at(510),
    toolCalls: [
      { id: 'tc-ask-scope', name: 'ask_user_question', input: { question: 'Which regions go in the deck?' }, output: '', status: 'complete', elapsedSeconds: 0.1 },
    ],
  },
  {
    id: 'm10b',
    role: 'assistant',
    content: '',
    timestamp: at(505),
    toolCalls: [
      { id: 'tc-ask-brands', name: 'ask_user_question', input: { question: 'Which brands should the comparison cover?' }, output: '', status: 'complete', elapsedSeconds: 0.1 },
    ],
  },
  {
    id: 'm11',
    role: 'assistant',
    content: "That variable wasn't found — the tree calls it `ageband1`. Retrying with the right name.",
    timestamp: at(500),
    toolCalls: [
      { id: 'tc-err', name: 'run_table_spec', input: { top: 'AgeBand', side: 'BrandConsideration(33)' }, output: 'Error: variable "AgeBand" not found in vartree', status: 'error', elapsedSeconds: 0.4 },
      { id: 'tc-ok', name: 'run_table_spec', input: { top: 'ageband1(*)', side: 'BrandConsideration(33)' }, output: JSON.stringify({ status: 'rendered', rows: 5 }), status: 'complete', elapsedSeconds: 1.7, hookEvents: [{ hookType: 'post_tool', timestamp: at(499), payload: { stdout: 'Table rendered with 5 rows, 3 columns' } }] },
    ],
  },
  {
    id: 'm12',
    role: 'assistant',
    content: "I've created a dashboard from the brand equity tables:",
    timestamp: at(490),
    toolCalls: [
      { id: 'tc-dash', name: 'create_dashboard', input: { name: 'Prius Brand Equity - TV Brand Performance' }, output: JSON.stringify({ dashboardId: DASHBOARD_CREATED.dashboardId }), status: 'complete', elapsedSeconds: 1.5 },
    ],
  },
  {
    id: 'm13',
    role: 'assistant',
    content: 'Building the deck now — this one was stopped part way through.',
    timestamp: at(480),
    stopped: true,
    toolCalls: [
      { id: 'tc-cancel', name: 'Bash', input: { command: 'python3 exports/build_deck.py' }, output: '', status: 'cancelled', elapsedSeconds: 3.2 },
    ],
  },
  {
    id: 'm14',
    role: 'assistant',
    content: "Here's a summary of everything so far:\n\n1. Analysed brand awareness across five regions\n2. Charted it and saved the chart\n3. Created a dashboard\n4. Started the PowerPoint export\n\nThe **North** (−1pp) may need attention. Scotland's **+5pp** is a strong signal worth a closer look.\n\n```python\nimport pandas as pd\ndf = pd.read_csv('awareness_by_region.csv')\nprint(df.describe())\n```",
    timestamp: at(470),
    autoArtifactPaths: ['exports/awareness-summary.pptx', 'exports/old-draft.pptx'],
  },
]

// The last turn while "streaming" is on: a chain still running, with the
// thinking block open and being written.
const streamingTail: AgentMessage[] = [
  {
    id: 'm15',
    role: 'assistant',
    thinking: 'Slide dimensions confirmed. Rebuild the deck with widescreen layouts, then check each chart fits its placeholder before saving.',
    content: '',
    timestamp: at(5),
    toolCalls: [
      { id: 'tc-live-1', name: 'get_toc', input: { customer: 'bprsky' }, output: '{}', status: 'complete', elapsedSeconds: 0.5 },
      { id: 'tc-live-2', name: 'Bash', input: { command: 'python3 exports/build_deck.py --widescreen' }, status: 'running' },
    ],
  },
]

// Chief of Staff mode: sub-agents and a workflow, folded from the events the
// runner sends through the one reducer, exactly as a chat folds them. The
// finished turn shows a sub-agent done (with its own tools and words), one
// failed and one stopped, and a finished workflow; the streaming tail shows a
// sub-agent and a workflow still at work.
const ev = (type: AgentSSEEvent['type'], data: Record<string, unknown>, secondsAgo: number): AgentSSEEvent =>
  ({ type, data, timestamp: at(secondsAgo) })

function foldDelegation(events: AgentSSEEvent[]): AgentMessage[] {
  return events.reduce(agentEventReducer, initialAgentEventState()).messages
}

const agentStart = (id: string, description: string, agentType: string, t: number): AgentSSEEvent[] => [
  ev('tool_use_start', { toolCallId: id, toolName: 'Agent', input: { description, subagent_type: agentType, prompt: description } }, t),
  ev('tool_use_end', { toolCallId: id, output: 'Async agent launched successfully.', isError: false }, t),
  ev('subagent_event', { event: 'start', toolUseId: id, taskId: `task-${id}`, description, agentType }, t),
]

// The Workflow tool's result names its run (`runId`); the reducer reads it, and
// the Agents section and drawer read that run's agents and their saved
// conversations through a `WorkflowAgentsSource`. On a real chat the source
// is goapi's two routes; here it is fixed data, so the panel and the drawer
// can be seen without a runner (design/2026-10-26-chief-of-staff-agents-panel.md, T9).
export const SHOWCASE_RUN_DONE = 'wf_showcase-done'
export const SHOWCASE_RUN_LIVE = 'wf_showcase-live'

const workflowLaunched = (taskId: string, workflowName: string, runId: string) =>
  JSON.stringify({ status: 'async_launched', taskId, workflowName, runId })

const SHOWCASE_RUNS: Record<string, WorkflowRun> = {
  [SHOWCASE_RUN_DONE]: {
    run_id: SHOWCASE_RUN_DONE,
    agents: [
      { id: 'a1f0c2d3e4', label: 'scan:tiktok', phase: 'Scan', model: 'sonnet', status: 'completed', result: '1.9M posts read, 21,400 mention Acme' },
      { id: 'a2f0c2d3e4', label: 'scan:x', phase: 'Scan', model: 'sonnet', status: 'completed', result: '1.6M posts read, 26,600 mention Acme' },
      { id: 'a3f0c2d3e4', label: 'code:mentions', phase: 'Code', model: 'sonnet', status: 'completed', result: '48,000 mentions coded; late delivery is the top theme at 31%' },
    ],
  },
  [SHOWCASE_RUN_LIVE]: {
    run_id: SHOWCASE_RUN_LIVE,
    agents: [
      { id: 'a4f0c2d3e4', label: 'scan:tiktok', phase: 'Scan', model: 'sonnet', status: 'completed', result: '1.9M posts read, 21,400 mention Acme' },
      { id: 'a5f0c2d3e4', label: 'scan:x', phase: 'Scan', model: 'sonnet', status: 'running' },
    ],
  },
}

function showcaseTranscript(agentId: string): WorkflowTranscript {
  const agent = Object.values(SHOWCASE_RUNS).flatMap(r => r.agents).find(a => a.id === agentId)
  const platform = agent?.label?.split(':')[1] ?? 'tiktok'
  const entries: WorkflowTranscript['entries'] = [
    { role: 'user', blocks: [{ type: 'text', text: `Read every ${platform} post from the connected accounts since 1 September and count the ones that mention Acme.` }] },
    { role: 'assistant', blocks: [
      { type: 'thinking', text: 'The posts are in the social job. A query by platform and brand mention gives the count directly.' },
      { type: 'tool_use', tool_use_id: `${agentId}-t1`, name: 'Bash', input: { command: `pt query --job acme-social "platform(${platform}) mentions(acme)"` } },
    ] },
    { role: 'user', blocks: [{ type: 'tool_result', tool_use_id: `${agentId}-t1`, text: platform === 'x' ? 'posts 1,600,214\nmention acme 26,611' : 'posts 1,904,377\nmention acme 21,402' }] },
  ]
  if (agent?.result) entries.push({ role: 'assistant', blocks: [{ type: 'text', text: agent.result }] })
  return { agent_id: agentId, entries, truncated: false }
}

export const SHOWCASE_WORKFLOW_SOURCE: WorkflowAgentsSource = {
  run: async (_sessionId, runId) => SHOWCASE_RUNS[runId] ?? { run_id: runId, agents: [] },
  transcript: async (_sessionId, _runId, agentId) => showcaseTranscript(agentId),
}

export const DELEGATION_DONE: AgentMessage[] = foldDelegation([
  ev('user_message', { id: 'cos-u1', content: 'Look across the social, survey and sales data and find the finding worth the most money.' }, 468),
  ev('message_start', { role: 'assistant', messageId: 'cos-m1' }, 467),
  ev('content_delta', { delta: "I'll put three agents on this in parallel and a workflow on the social scan." }, 467),
  ...agentStart('cos-a1', 'Size the complaint themes', 'general-purpose', 466),
  ...agentStart('cos-a2', 'Join survey to sales', 'general-purpose', 466),
  ...agentStart('cos-a3', 'Check churn by region', 'Explore', 466),
  ev('tool_use_start', { toolCallId: 'cos-w1', toolName: 'Workflow', input: { name: 'social-scan' } }, 466),
  ev('tool_use_end', { toolCallId: 'cos-w1', output: workflowLaunched('task-cos-w1', 'social-scan', SHOWCASE_RUN_DONE), isError: false }, 466),
  ev('workflow_event', { event: 'start', toolUseId: 'cos-w1', taskId: 'task-cos-w1', workflowName: 'social-scan', description: 'Scan every connected account for mentions of late deliveries' }, 466),
  ev('message_end', {}, 466),
  ev('message_start', { role: 'assistant', messageId: 'cos-s1', parentToolUseId: 'cos-a1' }, 465),
  ev('tool_use_start', { toolCallId: 'cos-n1', toolName: 'Bash', input: { command: 'pt query --job acme-social "theme(*)"' }, parentToolUseId: 'cos-a1' }, 465),
  ev('tool_use_end', { toolCallId: 'cos-n1', output: 'Late delivery 31%\nPrice 22%\nQuality 18%', isError: false, parentToolUseId: 'cos-a1' }, 463),
  ev('tool_use_start', { toolCallId: 'cos-n2', toolName: 'Bash', input: { command: 'pt query --job acme-sales "lost_revenue by reason"' }, parentToolUseId: 'cos-a1' }, 462),
  ev('tool_use_end', { toolCallId: 'cos-n2', output: 'late_delivery  £2.4m', isError: false, parentToolUseId: 'cos-a1' }, 460),
  ev('content_delta', { delta: 'Late delivery is the biggest complaint theme (**31%** of coded posts) and lines up with **£2.4m** of lost repeat orders.', parentToolUseId: 'cos-a1' }, 459),
  ev('message_end', { parentToolUseId: 'cos-a1' }, 459),
  ev('workflow_event', { event: 'progress', toolUseId: 'cos-w1', taskId: 'task-cos-w1', summary: 'Phase 2 of 3: coding 48,000 posts', lastToolName: 'Agent', usage: { total_tokens: 41000, tool_uses: 9, duration_ms: 50000 } }, 458),
  ev('subagent_event', { event: 'stop', toolUseId: 'cos-a1', taskId: 'task-cos-a1', status: 'completed', summary: 'Late delivery: 31% of complaints, £2.4m at risk', usage: { total_tokens: 18400, tool_uses: 2, duration_ms: 42000 } }, 457),
  ev('subagent_event', { event: 'stop', toolUseId: 'cos-a2', taskId: 'task-cos-a2', status: 'failed', summary: 'The sales warehouse refused the join: no customer key in common', usage: { total_tokens: 6200, tool_uses: 3, duration_ms: 21000 } }, 456),
  ev('workflow_event', { event: 'stop', toolUseId: 'cos-w1', taskId: 'task-cos-w1', status: 'completed', summary: '3.5M posts scanned, 48,000 mention Acme, all coded', usage: { total_tokens: 96000, tool_uses: 24, duration_ms: 185000 } }, 450),
  ev('subagent_event', { event: 'stop', toolUseId: 'cos-a3', taskId: 'task-cos-a3', status: 'stopped', summary: 'Stopped before it finished' }, 449),
  ev('message_start', { role: 'assistant', messageId: 'cos-m2' }, 448),
  ev('content_delta', { delta: 'Three of four came back. The finding worth the most is **late delivery**: about **£2.4m** a year in lost repeat orders. The survey-to-sales join failed, so that figure is not yet checked against the survey.' }, 448),
  ev('message_end', {}, 448),
])

export const DELEGATION_RUNNING: AgentMessage[] = foldDelegation([
  ev('message_start', { role: 'assistant', messageId: 'cos-live' }, 6),
  ev('content_delta', { delta: 'Checking the regional numbers while the scan runs.' }, 6),
  ...agentStart('cos-live-a', 'Rank regions by lost revenue', 'general-purpose', 5),
  ev('tool_use_start', { toolCallId: 'cos-live-w', toolName: 'Workflow', input: { name: 'social-scan' } }, 5),
  ev('tool_use_end', { toolCallId: 'cos-live-w', output: workflowLaunched('task-cos-live-w', 'social-scan', SHOWCASE_RUN_LIVE), isError: false }, 5),
  ev('workflow_event', { event: 'start', toolUseId: 'cos-live-w', taskId: 'task-cos-live-w', workflowName: 'social-scan' }, 5),
  ev('workflow_event', { event: 'progress', toolUseId: 'cos-live-w', taskId: 'task-cos-live-w', summary: 'Phase 1 of 3: reading TikTok and X', lastToolName: 'WebFetch' }, 3),
  ev('tool_use_start', { toolCallId: 'cos-live-n1', toolName: 'Bash', input: { command: 'pt query --job acme-sales "region(*)"' }, parentToolUseId: 'cos-live-a' }, 3),
  ev('subagent_event', { event: 'progress', toolUseId: 'cos-live-a', taskId: 'task-cos-live-a', summary: 'Querying sales by region' }, 2),
])

// While Streaming is on, the status walks this cycle so the living circle's
// transitions and its hover log can be seen (Adam, 13 Sep).
const STREAMING_CYCLE: ActivityStatus[] = [
  { label: 'Thinking…', category: 'thinking' },
  { label: 'Preparing: pt table', category: 'preparing_tool', toolName: 'Bash', toolInputPreview: 'pt table "TC Con All Prospect" --format json' },
  { label: 'Running: pt search', detail: 'consideration, TV', category: 'tool', toolName: 'Bash', toolInput: { command: 'pt search "consideration"' }, elapsedSeconds: 2 },
  { label: 'Running: build_deck.py', category: 'tool', toolName: 'Bash', elapsedSeconds: 4 },
  { label: 'Writing response…', category: 'writing' },
]
const STREAMING_CYCLE_MS = 2600

// The render-plugin events, keyed to the tool calls above by toolCallId. The
// plugins fold these exactly as they fold a live session's, so what appears
// under each tool call is the real widget with the real data.
//
// Typed through `unknown`: the core event union does not list host extension
// types (composite_table_rendered is Platinum's), which is the whole point of
// the plugin seam — the fold keys on the string, not the union.
const PLUGIN_EVENTS = [
  { type: 'table_rendered', data: TABLE_BY_GENDER, timestamp: at(580) },
  { type: 'chart_rendered', data: CHART_BY_AGE, timestamp: at(579) },
  { type: 'composite_table_rendered', data: COMPOSITE_TABLE, timestamp: at(545) },
  { type: 'provenance_validated', data: PROVENANCE_INITIAL, timestamp: at(544) },
  { type: 'provenance_validated', data: PROVENANCE_COMPLETE, timestamp: at(543) },
  { type: 'analysis_flagged', data: ANALYSIS_FLAGGED, timestamp: at(501) },
  { type: 'warehouse_query', data: WAREHOUSE_QUERY_OK, timestamp: at(500) },
  { type: 'warehouse_query', data: WAREHOUSE_QUERY_ERROR, timestamp: at(499) },
  { type: 'table_rendered', data: TABLE_BY_AGE, timestamp: at(500) },
  { type: 'dashboard_created', data: DASHBOARD_CREATED, timestamp: at(490) },
  // Two revisions the agent made for the Notebook (#1869). Their tool calls
  // are not in the conversation above, so the chat draws nothing for them;
  // the Notebook shows the table's as confirmed and the query's as pending.
  { type: 'table_rendered', data: { ...TABLE_BY_AGE, id: 'table_rendered-tc-table-rev-0', toolCallId: 'tc-table-rev', title: 'Sky Consideration Rate by Age', revises: 'tc-table:0' }, timestamp: at(480) },
  { type: 'warehouse_query', data: { ...WAREHOUSE_QUERY_OK, id: 'warehouse_query-tc-wh-rev-0', toolCallId: 'tc-wh-rev', sql: WAREHOUSE_QUERY_OK.sql.replace("week = '2026-W40'", "week IN ('2026-W39', '2026-W40')"), revises: WAREHOUSE_QUERY_OK.id }, timestamp: at(470) },
] as unknown as AgentSSEEvent[]

// The Notebook's stored part for the showcase: a note after the composite,
// the table revision confirmed, an unsent SQL edit on the refused query.
const SHOWCASE_NOTEBOOK_STATE: NotebookState = {
  version: 3,
  notes: [{ id: 'note-showcase-1', text: 'The gap is widest for women. Check the base before we quote it.', after: COMPOSITE_TABLE.id, created_at: 1 }],
  decisions: { 'table_rendered-tc-table-rev-0': 'confirmed' },
  edits: { [WAREHOUSE_QUERY_ERROR.id]: { sql: "SELECT region, COUNT(*) AS events\nFROM `sky-demo.viewing.events`\nWHERE week = '2026-W40'\nGROUP BY region" } },
}
export const SHOWCASE_NOTEBOOK_CELLS = deriveNotebookCells(PLUGIN_EVENTS, SHOWCASE_NOTEBOOK_STATE)
const showcaseLoadDataset = () => Promise.reject(new Error('the showcase has no session files'))
const showcaseLoadScript = () => Promise.resolve(BRAND_GAP_SCRIPT)
const initialQuestions = new Map<string, AskUserQuestionInfo>([
  ['tc-ask-format', {
    question: 'Which slide format would you like for the PowerPoint export?',
    context: 'This sets the slide dimensions and the layout of every chart.',
    options: [
      { label: 'Standard (4:3)', value: 'standard' },
      { label: 'Widescreen (16:9)', value: 'widescreen' },
      { label: 'Print (A4)', value: 'print' },
    ],
    allowFreetext: false,
    toolCallId: 'tc-ask-format',
    answered: true,
    selectedValue: 'widescreen',
  }],
  ['tc-ask-scope', {
    question: 'Which regions go in the deck?',
    context: '',
    options: [
      { label: 'All five', value: 'all', description: 'One slide per region, plus the summary' },
      { label: 'Top three', value: 'top3', description: 'London, South East and Midlands only' },
      { label: 'Just the summary', value: 'summary', description: 'One slide with the comparison table' },
      { label: 'Continue without a deck', value: 'skip', advance: true },
    ],
    allowFreetext: true,
    toolCallId: 'tc-ask-scope',
    answered: false,
  }],
  // Multi-select — the ask_user DEFAULT (Kai, 11 Sep): toggles and one Submit,
  // which sends one message listing the chosen values (Matt, 11 Sep — "pick
  // any"). The two questions above show the multi_select: false opt-out, for
  // options that cannot combine (one slide format, one scope).
  ['tc-ask-brands', {
    question: 'Which brands should the comparison cover?',
    context: 'Pick any — they all come from the same consideration table.',
    options: [
      { label: 'Sky', value: 'Sky' },
      { label: 'Virgin Media', value: 'Virgin Media' },
      { label: 'BT', value: 'BT' },
      { label: 'TalkTalk', value: 'TalkTalk' },
      { label: 'None of these', value: 'none of these' },
    ],
    allowFreetext: true,
    multiSelect: true,
    toolCallId: 'tc-ask-brands',
    answered: false,
  }],
])

const artifacts: ArtifactInfo[] = [
  { filePath: 'exports/awareness-summary.pptx', fileName: 'awareness-summary.pptx', artifactType: 'file', label: 'Awareness summary deck', description: 'PowerPoint export, widescreen', source: 'auto', status: 'live' },
  { filePath: 'exports/old-draft.pptx', fileName: 'old-draft.pptx', artifactType: 'file', label: 'Earlier draft', description: 'Replaced by the summary deck', source: 'auto', status: 'lost' },
  { filePath: 'charts/awareness_by_region.svg', fileName: 'awareness_by_region.svg', artifactType: 'image', label: 'Awareness chart', source: 'auto', status: 'live' },
  // The data files the render and compose tools register, named as they name
  // them, so the Files panel shows a table, a chart and a composite as three
  // different things (Matt, 10 Sep).
  { filePath: 'data/table_gender_x_BrandConsideration_1.json', fileName: 'table_gender_x_BrandConsideration_1.json', artifactType: 'data', label: 'Sky consideration rate by gender (data)', description: 'Table data (PlatinumData JSON)', source: 'registered', status: 'live' },
  { filePath: 'data/chart_ageband1_x_BrandConsideration_2.json', fileName: 'chart_ageband1_x_BrandConsideration_2.json', artifactType: 'data', label: 'Sky consideration by age group (data)', description: 'Chart data (PlatinumData JSON)', source: 'registered', status: 'live' },
  { filePath: 'data/brand_index.json', fileName: 'brand_index.json', artifactType: 'data', label: 'Brand index (composite)', description: 'Composite table with per-cell provenance (PlatinumData JSON)', source: 'registered', status: 'live' },
  { filePath: 'analysis/brand_report.py', fileName: 'brand_report.py', artifactType: 'code', label: 'brand_report.py', source: 'auto', status: 'live' },
]

const todos: TodoItem[] = [
  { content: 'Load the brand awareness table', status: 'completed' },
  { content: 'Run the regional crosstab', status: 'completed' },
  { content: 'Chart it', status: 'completed' },
  { content: 'Build the PowerPoint export', status: 'in_progress' },
  { content: 'Send the summary', status: 'pending' },
]

const nowSec = Math.floor(Date.now() / 1000)
const session = (id: string, title: string, user_email: string, hoursAgo: number, status = 'completed', artifact_count = 2): AgentSessionListItem => ({
  id, title, user_email, status, artifact_count,
  customer: CUSTOMER, job: JOB, workflow_id: '', persona: '', current_node: '',
  created_at: nowSec - hoursAgo * 3600, updated_at: nowSec - hoursAgo * 3600 + 900,
})
const SESSIONS: AgentSessionListItem[] = [
  session(SESSION_ID, 'Brand awareness by region', 'you@bayesprice.com', 0.2, 'active', 3),
  session('s-2', 'Sky consideration funnel, Q2', 'you@bayesprice.com', 5),
  session('s-3', 'TikTok sentiment deep dive', 'matt@bayesprice.com', 26, 'completed', 7),
  session('s-4', 'Quarterly brand health check', 'nikki@bayesprice.com', 50),
  session('s-5', 'Prius exec report draft', 'you@bayesprice.com', 120, 'completed', 1),
]
const SESSION_USERS = ['you@bayesprice.com', 'matt@bayesprice.com', 'nikki@bayesprice.com']

const noop = () => {}

// The side pane (SidePaneChat through ChatDrawer), with every row shape an
// Insights drawer draws: framing, a markdown table, a system line, a
// render_graph chart under its row, prompts, a persona, a tray above the
// composer. Loading follows the Streaming switch.
const SIDE_PANE_MESSAGES: ChatMessage[] = [
  { role: 'assistant', content: 'This table shows **brand awareness** by region, among all adults.\n\n| Region | Aware | Base |\n|---|---:|---:|\n| London | 85% | 412 |\n| South East | 79% | 388 |\n| Scotland | 71% | 203 |\n\nLondon leads; Scotland trails the average by 6 points.' },
  { role: 'user', content: 'Show that as a chart and tell me which gap matters.' },
  { role: 'system', content: 'Extracted 12 themes from the data' },
  {
    role: 'assistant',
    content: 'Here it is. The gap that matters is **London vs Scotland**: 14 points on bases above 200.',
    tool_calls: [{ id: 'showcase-graph', type: 'function', function: { name: 'render_graph', arguments: '{}' } }],
  },
]
const SIDE_PANE_PROMPTS = ['Summarise this table', 'Which differences are significant?', 'Chart awareness by region']
const SIDE_PANE_PERSONAS = ['Executive Summarizer', 'Table Analyst']
const SIDE_PANE_FRAMING = ['Sources', 'Brand awareness by region · all adults · Aug-26']

function showcaseRenderMessage(message: ChatMessage): React.ReactNode | undefined {
  if (!message.tool_calls?.some(tc => tc.function.name === 'render_graph')) return undefined
  return (
    <Box sx={{ ...CARD_SX, p: 1.5 }}>
      <Box component="img" alt="Brand awareness by region" src={`data:image/svg+xml;base64,${CHART_SVG_BASE64}`} sx={{ width: '100%', display: 'block' }} />
    </Box>
  )
}

/**
 * The New alert card (alerts round, 12 Oct) as the start screen draws it: a
 * filled draft from fixed data, every handler a no-op, no network.
 */
const ALERT_TABLE_NODES: GenNode[] = [
  { type: 'Table', id: 1, parentId: 0, isExpanded: false, isFolder: false, value1: 'intent_to_purchase.cbt', value2: 'Brand health', level: 0, anyChildren: false },
  { type: 'Table', id: 2, parentId: 0, isExpanded: false, isFolder: false, value1: 'consideration.cbt', value2: 'Brand health', level: 0, anyChildren: false },
  { type: 'Table', id: 3, parentId: 0, isExpanded: false, isFolder: false, value1: 'awareness_by_region.cbt', value2: 'Awareness', level: 0, anyChildren: false },
]
const ALERT_DRAFT: AlertSetupDraft = {
  name: 'Sky ITP below 60%',
  kind: 'table',
  job: JOB,
  tables: [
    { job: JOB, folder_path: 'Brand health', filename: 'intent_to_purchase.cbt', display_name: 'Intent to purchase' },
    { job: JOB, folder_path: 'Brand health', filename: 'consideration.cbt', display_name: 'Consideration' },
  ],
  question: '',
  condition: "Sky's intent to purchase falls below 60% in the latest month",
  context: '',
  schedule: { every: 'weekly', weekday: 1, at: '09:00', timezone: 'Europe/London' },
  recipients: ['matt@bayesprice.com'],
}

function AlertCardGallery() {
  return (
    <Box data-testid="alert-card-gallery" sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.default', flexShrink: 0, maxHeight: '60%', overflow: 'auto' }}>
      <Box sx={{ maxWidth: AGENT_COLUMN_MAX_WIDTH, mx: 'auto' }}>
        <AlertSetupCard
          draft={ALERT_DRAFT}
          isNew
          problems={{}}
          jobs={[{ name: JOB, displayName: 'Sky UK Brand Equity' }]}
          toc={ALERT_TABLE_NODES}
          tocLoading={false}
          recipientOptions={[{ email: 'matt@bayesprice.com', name: 'Matt Gibbs' }]}
          recipientsLoading={false}
          recipientsError={null}
          recipientQuery=""
          loading={false}
          loadError={null}
          disabled={false}
          onChange={noop}
          onKindChange={noop}
          onJobChange={noop}
          onAddTable={noop}
          onRemoveTable={noop}
          onRecipientQueryChange={noop}
        />
      </Box>
    </Box>
  )
}

function SidePaneTray() {
  return (
    <Box data-testid="showcase-side-pane-tray" sx={{ border: HAIRLINE, borderRadius: CARD_SX.borderRadius, p: 1.5, fontSize: 12, color: 'text.secondary' }}>
      Above the composer: a helper link or a tray (the Theme AI proposed-themes tray sits here).
    </Box>
  )
}

/**
 * Every orb state at both sizes on a light and a dark ground, and the
 * mapping the status line uses (Adam, 13 Sep: libraries.dev/orbs). The
 * activity categories are the real ones from the agent's activity status.
 */
const ORB_STATES: OrbState[] = ['working', 'searching', 'solving', 'listening', 'connecting', 'weaving', 'composing', 'breathing', 'shaping']
const ORB_MAPPING: Array<{ label: string; status: Parameters<typeof orbStateFor>[0] }> = [
  { label: 'Reasoning', status: { category: 'thinking' } },
  { label: 'Writing a tool call', status: { category: 'preparing_tool' } },
  { label: 'Running a tool', status: { category: 'tool', toolName: 'Bash', toolInput: { command: 'python build_deck.py' } } },
  { label: 'Searching', status: { category: 'tool', toolName: 'Bash', toolInput: { command: 'pt search "consideration"' } } },
  { label: 'Writing the reply', status: { category: 'writing' } },
  { label: 'Session starting', status: { category: 'system' } },
]

function OrbGallery() {
  const ground = (theme: 'light' | 'dark') => (
    <Box key={theme} sx={{ flex: 1, minWidth: 320, p: 1.5, borderRadius: 2, backgroundColor: theme === 'dark' ? '#0f172a' : '#ffffff', color: theme === 'dark' ? '#e2e8f0' : '#0A1628', border: '1px solid', borderColor: theme === 'dark' ? '#1e293b' : '#e2e8f0' }}>
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {ORB_STATES.map(state => (
          <Box key={state} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
            <ActivityOrb state={state} size={64} theme={theme} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12 }}>
              <ActivityOrb state={state} size={20} theme={theme} />
              <span>{state}</span>
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: theme === 'dark' ? '#334155' : '#e2e8f0', fontSize: 12 }}>
        {ORB_MAPPING.map(m => (
          <Box key={m.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ActivityOrb status={m.status} size={20} theme={theme} />
            <span>{m.label}</span>
            <Box component="span" sx={{ opacity: 0.55 }}>· {orbStateFor(m.status)}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
  return (
    <Box data-testid="orb-gallery" sx={{ display: 'flex', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.default', flexShrink: 0, flexWrap: 'wrap' }}>
      {ground('light')}
      {ground('dark')}
    </Box>
  )
}

export default function AgentDesignShowcase() {
  const [streaming, setStreaming] = useState(false)
  // The cycling status: a timer while Streaming is on, cleared when it is off.
  const [cycleTick, setCycleTick] = useState(0)
  useEffect(() => {
    if (!streaming) return undefined
    const t = setInterval(() => setCycleTick(n => n + 1), STREAMING_CYCLE_MS)
    return () => clearInterval(t)
  }, [streaming])
  const streamingStatus = STREAMING_CYCLE[cycleTick % STREAMING_CYCLE.length]
  const [readOnly, setReadOnly] = useState(false)
  const [fullWidth, setFullWidth] = useState(false)
  const [squareTop, setSquareTop] = useState(false)
  const [orbs, setOrbs] = useState(false)
  const [refused, setRefused] = useState(false)
  const [sidePane, setSidePane] = useState(false)
  const [alertCard, setAlertCard] = useState(false)
  const [paneMessages, setPaneMessages] = useState(SIDE_PANE_MESSAGES)
  const [persona, setPersona] = useState(SIDE_PANE_PERSONAS[0])
  const [model, setModel] = useState(MODELS[0].id)
  const [questions, setQuestions] = useState(initialQuestions)
  // Both panels, wired as the chat page wires them: the left one carries the
  // real Jobs / Tables / Variables tabs against the job (so a tick in the tree
  // chips the composer for real) and a fixed session history; the right one
  // is AgentChat's own Files panel over the showcase's artifacts.
  const [leftOpen, setLeftOpen] = useState(false)
  const [leftTab, setLeftTab] = useState<string | undefined>(undefined)
  const [rightOpen, setRightOpen] = useState(false)
  const [preview, setPreview] = useState<ComposerChip | null>(null)
  const { chips, chipIds, removeChip, toggleChip, clear, buildMessage, suggestedPrompt } = useComposerChips()
  const account = useAccount()
  const labelJob = useJobLabel(CUSTOMER)
  const currentUserEmail = account.user?.session_info?.email || 'you@bayesprice.com'

  const sidePanelTabs: AgentSidePanelTab[] = useMemo(() => buildAgentSidePanelTabs({
    customer: CUSTOMER,
    chipIds,
    onToggle: toggleChip,
    onPreview: setPreview,
    useValues: () => ({ sessionId: SESSION_ID, sessionJobs: [JOB], fallbackJobs: [JOB], job: JOB, treeJobs: [JOB] }),
  }), [chipIds, toggleChip])

  const composer = useMemo(() => ({
    chips: chips.map(c => ({ id: c.id, label: c.label, tooltip: c.tooltip })),
    onRemoveChip: removeChip,
    onChipClick: (id: string) => setPreview(chips.find(c => c.id === id) ?? null),
    suggestedText: suggestedPrompt,
    buildMessage,
    onSent: clear,
  }), [chips, removeChip, suggestedPrompt, buildMessage, clear])

  const openLeft = useCallback((tab: string) => {
    setLeftTab(tab)
    setLeftOpen(true)
  }, [])
  const revealLeft = useCallback(() => setLeftOpen(true), [])
  const closeLeft = useCallback(() => setLeftOpen(false), [])
  const openRight = useCallback(() => setRightOpen(true), [])
  const closeRight = useCallback(() => setRightOpen(false), [])

  const shown = streaming
    ? [...messages, ...DELEGATION_DONE, ...streamingTail, ...DELEGATION_RUNNING]
    : [...messages, ...DELEGATION_DONE]

  // The Notebook (#1869) over fixed cells. Nothing is saved: only opening,
  // closing and the tick boxes change; Read only shows a colleague's view.
  const [notebookOpen, setNotebookOpen] = useState(false)
  const [notebookFocus, setNotebookFocus] = useState<string | undefined>(undefined)
  const [notebookTicked, setNotebookTicked] = useState<string[]>([])
  const openNotebook = useCallback((cellId?: string) => {
    setNotebookFocus(cellId)
    setNotebookOpen(true)
  }, [])
  const notebookHost = useMemo(() => ({ ...DEFAULT_CARD_HOST, openNotebook }), [openNotebook])
  const notebook: NotebookController = {
    open: notebookOpen,
    focusCellId: notebookFocus,
    cells: SHOWCASE_NOTEBOOK_CELLS,
    state: SHOWCASE_NOTEBOOK_STATE,
    loading: false,
    loadError: '',
    saveError: '',
    canEdit: !readOnly,
    hasComposer: !readOnly,
    ticked: notebookTicked,
    runs: {},
    running: {},
    runErrors: {},
    handleOpen: openNotebook,
    handleClose: () => setNotebookOpen(false),
    handleAddNote: noop,
    handleEditNote: noop,
    handleDeleteNote: noop,
    handleDecide: noop,
    handleEdit: noop,
    handleClearEdit: noop,
    handleRunTable: async () => {},
    handleToggleTick: (id) => setNotebookTicked(t => (t.includes(id) ? t.filter(x => x !== id) : [...t, id])),
    handleSendChanges: noop,
    handleBuildWebapp: noop,
  }
  const notebookButton = (
    <Tooltip title="Notebook">
      <IconButton size="small" aria-label="Notebook" data-testid="agent-notebook-button" onClick={() => openNotebook()} sx={{ width: 34, height: 34, flexShrink: 0, color: 'info.main' }}>
        <NotesOutlinedIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>
  )

  const handleAnswer = (toolCallId: string, value: string) => {
    setQuestions(prev => {
      const next = new Map(prev)
      const q = next.get(toolCallId)
      if (q) next.set(toolCallId, { ...q, answered: true, selectedValue: value })
      return next
    })
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        data-testid="design-showcase-bar"
        sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper', flexShrink: 0 }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Design showcase</Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Every message shape, from fixed data. The table, chart, composite table and dashboard are the real widgets on bprsky / sky-uk-be-prius.
        </Typography>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel control={<Switch size="small" checked={streaming} onChange={e => setStreaming(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Streaming</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={readOnly} onChange={e => setReadOnly(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Read only</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={fullWidth} onChange={e => setFullWidth(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Full width</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={leftOpen} onChange={e => setLeftOpen(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Left panel</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={rightOpen} onChange={e => setRightOpen(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Right panel</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={orbs} onChange={e => setOrbs(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Orbs</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={refused} onChange={e => setRefused(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Refused</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={sidePane} onChange={e => setSidePane(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Side pane</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={alertCard} onChange={e => setAlertCard(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Alert card</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={notebookOpen} onChange={e => (e.target.checked ? openNotebook() : setNotebookOpen(false))} slotProps={{ input: { 'aria-label': 'Notebook' } }} data-testid="showcase-notebook-switch" />} label={<Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>Notebook</Typography>} />
      </Box>
      {orbs && <OrbGallery />}
      {alertCard && <AlertCardGallery />}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* The left-edge pill and the launcher both open the panel (Adam, 13 Oct). */}
        <EdgeReveal
          side="left"
          label="Open tables panel"
          icon={<VerticalSplitOutlinedIcon />}
          hidden={leftOpen}
          onOpen={revealLeft}
        />
        <SidePanelLauncher tabs={sidePanelTabs} hidden={leftOpen} onOpen={openLeft} />
        <AgentSidePanel
          open={leftOpen}
          onClose={closeLeft}
          tabs={sidePanelTabs}
          activeKey={leftTab}
          onActiveKeyChange={setLeftTab}
          sessions={SESSIONS}
          activeSessionId={SESSION_ID}
          onSelectSession={noop}
          hasMore={false}
          users={SESSION_USERS}
          selectedUserEmail="me"
          onUserFilterChange={noop}
          searchQuery=""
          onSearchChange={noop}
          onSearchSubmit={noop}
          isSearching={false}
          currentUserEmail={currentUserEmail}
          labelJob={labelJob}
        />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <AgentCardHostContext.Provider value={notebookHost}>
        <WorkflowAgentsSourceContext.Provider value={SHOWCASE_WORKFLOW_SOURCE}>
        <AgentChat
          sessionId={SESSION_ID}
          apiBaseUrl=""
          messages={shown}
          isStreaming={streaming}
          activityStatus={streaming ? streamingStatus : null}
          error={null}
          onSendMessage={noop}
          onCancel={noop}
          artifacts={artifacts}
          todos={todos}
          askedQuestions={questions}
          plugins={AGENT_PLUGINS}
          pluginEvents={PLUGIN_EVENTS}
          onAnswerQuestion={handleAnswer}
          models={MODELS}
          selectedModel={model}
          onModelChange={setModel}
          readOnly={readOnly}
          forkedMessageCount={2}
          composer={composer}
          artifactPanelOpen={rightOpen}
          onOpenArtifactPanel={openRight}
          onCloseArtifactPanel={closeRight}
          sheet={{ maxWidth: AGENT_COLUMN_MAX_WIDTH, fullWidth, squareTop: squareTop || (leftOpen && rightOpen) }}
          // #1537 PR3: the refused case — a red-bordered notice as the first
          // row of the conversation, the composer closed and saying why. Same
          // shape whether the refusal is a level or a team budget.
          composerDisabled={refused}
          composerPlaceholder={refused ? "Your team's budget is used. Please contact Alex Realm for more information." : undefined}
          notice={refused ? {
            severity: 'error',
            text: "Your team's budget is used. Please contact Alex Realm for more information.",
          } : undefined}
          composerLeading={notebookButton}
        />
        </WorkflowAgentsSourceContext.Provider>
        </AgentCardHostContext.Provider>
        </Box>
      </Box>
      <AgentAttachmentPreview chip={preview} customer={CUSTOMER} onClose={() => setPreview(null)} />
      <NotebookDialog
        notebook={notebook}
        customer={CUSTOMER}
        sessionId={SESSION_ID}
        readOnlyReason={readOnly ? refuse('You can’t edit this chat’s notebook.', NOBODY_KNOWN, { owner: 'matt@bayesprice.com' }).reason : undefined}
        queryActionReason={warehouseCardActionReason(DEFAULT_CARD_HOST)}
        loadDataset={showcaseLoadDataset}
        loadScript={showcaseLoadScript}
        onOpenInChat={(toolCallId) => {
          setNotebookOpen(false)
          requestAnimationFrame(() => document.querySelector(`[data-tool-call-id="${CSS.escape(toolCallId)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
        }}
      />
      <ChatDrawer
        open={sidePane}
        onClose={() => setSidePane(false)}
        messages={paneMessages}
        onSendMessage={(_model, input) => setPaneMessages(prev => [...prev, { role: 'user', content: input }])}
        onResetChat={() => setPaneMessages(SIDE_PANE_MESSAGES)}
        isLoading={streaming}
        loadingTitle="Reading the table and writing a summary…"
        error="The model did not answer in time. Try again."
        scopeLabel="Brand awareness by region"
        framing={SIDE_PANE_FRAMING}
        examplePrompts={SIDE_PANE_PROMPTS}
        renderMessage={showcaseRenderMessage}
        personas={SIDE_PANE_PERSONAS}
        selectedPersona={persona}
        onPersonaChange={setPersona}
        inputHelper={<SidePaneTray />}
      />
    </Box>
  )
}
